/**
 * TravelBufferWarning — calm amber inline banner shown when a
 * proposed lesson slot creates a tight travel gap from an
 * adjacent lesson.
 *
 * DESIGN PASS — STATIC MOCKUP ONLY. Feb 18, 2026.
 *
 * Doctrine sources:
 *   /app/memory/TRAVEL_AWARE_SCHEDULING_PROPOSAL.md §4 §5
 *   /app/memory/TRAVEL_AWARE_WIREFRAME_PASS.md §2
 *   /app/memory/P4_OPERATIONAL_INTELLIGENCE_GATE.md §6 (Rules T1, A2)
 *
 * What this component does:
 *   - Renders the operator-locked warning copy.
 *   - Provides two affordances (Adjust slot / Continue anyway).
 *   - Renders an override receipt after "Continue anyway".
 *
 * What this component does NOT do (design-only pass):
 *   - No API calls.
 *   - No persistence.
 *   - No buffer math.
 *   - No state propagation to a parent calendar.
 */
import { useState } from "react";
import { Clock } from "lucide-react";

export default function TravelBufferWarning({
  travelMinutes = 23,
  bufferMinutes = 10,
  availableMinutes = 18,
  fromLocation = "Wimbledon",
  toLocation = "Putney",
  onAdjust,
}) {
  const [overridden, setOverridden] = useState(false);

  if (overridden) {
    return (
      <div
        data-testid="travel-warning-override-receipt"
        className="text-[12px] text-slate-500 px-1"
      >
        Travel buffer overridden. {availableMinutes} min gap.
      </div>
    );
  }

  const requiredMinutes = travelMinutes + bufferMinutes;

  return (
    <div
      data-testid="travel-warning-banner"
      className="bg-amber-50 rounded-md border border-amber-200 p-4 flex flex-col gap-3"
    >
      <div className="flex items-start gap-2">
        <Clock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-amber-900">
            Tight travel from previous lesson
          </p>
          <p className="text-sm text-amber-800 mt-1 leading-relaxed">
            <span className="tabular-nums">{travelMinutes}</span> min
            travel between {fromLocation} and {toLocation} +{" "}
            <span className="tabular-nums">{bufferMinutes}</span> min
            buffer needed; only{" "}
            <span className="tabular-nums">{availableMinutes}</span>{" "}
            min available.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
        <button
          type="button"
          data-testid="travel-warning-adjust-btn"
          onClick={() => onAdjust && onAdjust()}
          className="inline-flex items-center justify-center text-sm font-bold text-slate-900 bg-white border border-slate-300 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Adjust slot
        </button>
        <button
          type="button"
          data-testid="travel-warning-continue-btn"
          onClick={() => setOverridden(true)}
          className="inline-flex items-center justify-center text-sm font-bold text-slate-700 px-4 py-2 rounded-lg hover:bg-amber-100/50 transition-colors"
        >
          Continue anyway
        </button>
      </div>

      <p className="text-[11px] text-amber-700/80 leading-snug">
        Required: <span className="tabular-nums">{requiredMinutes}</span>{" "}
        min total.
      </p>
    </div>
  );
}
